import {
  parseWarehouseModel,
  WarehouseInterface,
  WarehouseResponseInterface,
} from './WarehouseModel'
import { parsePartnerModel, PartnerInterface, PartnerResponseInterface } from './PartnerModel'
import { formatDate, parseEnumValue, parseNumber, parseString } from './helpers'

export enum OrderStatus {
  collecting = 'collecting',
  pending = 'pending',
  hold = 'hold',
  cancel = 'cancel',
  collected = 'collected',
  finished = 'finished',
}

export enum OrderType {
  all = 'all',
  sale = 'sale',
  outbound = 'outbound',
  inbound = 'inbound',
  inventory = 'inventory',
  order = 'order',
  backorder = 'backorder',
}

export interface OrderInterface {
  id: string
  order_number: string
  user_id: string
  warehouse_id: string
  partner_from_id: string
  partner_to_id: string
  type: OrderType | null
  status: OrderStatus | null
  info: string
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  external_no: string | null
  document_date: string | null
  order_title: string | null
  warehouse: WarehouseInterface
  partner_from: PartnerInterface
  partner_to: PartnerInterface
}

export interface OrderResponseInterface {
  id: string
  order_number: string
  user_id: string
  warehouse_id: string
  partner_from_id: string
  partner_to_id: string
  type: string
  status: string
  info: string
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  external_no: string | null
  document_date: string | null
  order_title: string | null
  warehouse: WarehouseResponseInterface
  partner_from: PartnerResponseInterface
  partner_to: PartnerResponseInterface
}

export function parseOrderModel(response: OrderResponseInterface): OrderInterface {
  return {
    id: parseString(response?.id),
    order_number: parseString(response?.order_number),
    user_id: parseString(response?.user_id),
    warehouse_id: parseString(response?.warehouse_id),
    partner_from_id: parseString(response?.partner_from_id),
    partner_to_id: parseString(response?.partner_to_id),
    type: parseEnumValue(OrderType, response?.type),
    status: parseEnumValue(OrderStatus, response?.status),
    info: parseString(response?.info),
    created_at: formatDate(response?.created_at) || null,
    updated_at: formatDate(response?.updated_at) || null,
    deleted_at: formatDate(response?.deleted_at) || null,
    external_no: parseString(response?.external_no, null),
    document_date: parseString(response?.document_date, null),
    order_title: parseString(response?.order_title, null),
    warehouse: parseWarehouseModel(response?.warehouse),
    partner_from: parsePartnerModel(response?.partner_from),
    partner_to: parsePartnerModel(response?.partner_to),
  }
}

export interface OrderValidationResponse {
  message: string | null
  type: string | null
}

export function parseOrderValidationModel(
  response: OrderValidationResponse
): OrderValidationResponse {
  return {
    message: parseString(response?.message),
    type: parseString(response?.type) || 'error',
  }
}

export interface OrderTypeSummaryResponseItem {
  title: string
  count: number
}

export type OrderTypeSummaryResponse = Partial<Record<OrderType, OrderTypeSummaryResponseItem>>

export interface OrderTypeSummaryResponseItem {
  title: string
  count: number
}

export interface OrderTypeSummaryItem {
  type: OrderType
  title: string
  count: number
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function getSummaryItemByType(
  summary: OrderTypeSummaryItem[],
  type: string
): OrderTypeSummaryItem | null {
  return summary.find((item) => item.type === type) ?? null
}

export function parseOrderTypeSummary(data: unknown): OrderTypeSummaryItem[] {
  if (!isObject(data)) {
    return []
  }

  return Object.values(OrderType)
    .map((type) => {
      const rawItem = isObject(data[type]) ? data[type] : null

      if (!rawItem) {
        return null
      }

      return {
        type,
        title: parseString(rawItem.title),
        count: parseNumber(rawItem.count),
      }
    })
    .filter((item): item is OrderTypeSummaryItem => item !== null)
}
